import { useTranslation } from 'react-i18next';
import { AlertTriangle, RotateCcw } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

import { CONFIRM_ARRIVAL_UNIT_MODE } from '../model/constants';
import type { ConfirmArrivalUnitMode } from '../model/types';

interface ConfirmArrivalUnitToolbarProps {
  /** The table-wide Inventory / Handling switch — offered only when a line has a bundle to convert to. */
  showUnitSwitch: boolean;
  unitMode: ConfirmArrivalUnitMode;
  onUnitModeChange: (mode: ConfirmArrivalUnitMode) => void;
  onResetQuantities: () => void;
  /** Any handling-unit figure on screen — the table's unit or an open second input. */
  showsHandlingFigures: boolean;
}

/** Above a quantity table: the unit switch, "reset quantities", and legacy's estimate warning. */
export function ConfirmArrivalUnitToolbar({
  showUnitSwitch,
  unitMode,
  onUnitModeChange,
  onResetQuantities,
  showsHandlingFigures
}: ConfirmArrivalUnitToolbarProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {showUnitSwitch ? (
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={unitMode}
            onValueChange={(value) => value && onUnitModeChange(value as ConfirmArrivalUnitMode)}
            aria-label={t('receive.confirm.unitMode.label', 'Quantity unit')}>
            <ToggleGroupItem value={CONFIRM_ARRIVAL_UNIT_MODE.INVENTORY}>
              {t('requestline.ltuQty.short', 'Inventory Units')}
            </ToggleGroupItem>
            <ToggleGroupItem value={CONFIRM_ARRIVAL_UNIT_MODE.HANDLING}>
              {t('bundle.bundleQty.label', 'Handling Units')}
            </ToggleGroupItem>
          </ToggleGroup>
        ) : (
          <span />
        )}

        <Button type="button" variant="ghost" size="sm" onClick={onResetQuantities}>
          <RotateCcw className="mr-2 h-4 w-4" />
          {t('dispatch.resetqtys.button', 'reset quantities')}
        </Button>
      </div>

      {showsHandlingFigures && (
        <Alert variant="warning">
          <AlertTriangle />
          <AlertDescription>
            {t(
              'disposition.bundlewarn.desc',
              'Warning! The displayed quantities are informative and might be not accurate (quantities in handling units are estimated based on the consignment line configuration). For exact quantities please switch back to Inventory units!'
            )}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
